import { Table, Button, DropdownButton, Dropdown, Pagination } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useJwt } from "react-jwt";

import Header from "../../components/MarginalsV2/Header";
import Footer from "../../components/MarginalsV2/Footer";
import "./style.css";
import useStateWithLocalStorage from "../../helpers/useStateWithLocalStorage";
import { Icon } from "@iconify/react";
import { useAuth } from '../../contexts/AuthContext';

// Default pagination settings
const DEFAULT_PAGINATION = {
  currentPage: 1,
  totalPages: 1,
  totalDocuments: 0,
  documentsPerPage: 50,
  hasNextPage: false,
  hasPrevPage: false
};

export default function UserAccount() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { lang } = useParams();
  const { token, userIsUnauthenticated } = useAuth();
  const [documentId, setDocumentId] = useStateWithLocalStorage("documentId");
  const [documents, setDocuments] = useState([]);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [isLoading, setIsLoading] = useState(false);
  const [searchEmail, setSearchEmail] = useState("");
  const { decodedToken, isExpired } = useJwt(token);
  const isAdmin = decodedToken && decodedToken.isAdmin;
  const hasValidToken = !!token && token !== "null" && token !== "undefined";

  const productIcons = {
    regular_idp: "simple-icons:johndeere",
    express_idp: "fluent:access-time-24-regular",
    emergency_idp: "bi:lightning",
  };

  useEffect(() => {
    if (!hasValidToken) return;

    setDocumentId("");
    fetchDocuments(DEFAULT_PAGINATION.currentPage, DEFAULT_PAGINATION.documentsPerPage);
  }, [hasValidToken]);

  const fetchDocuments = async (page = pagination.currentPage, limit = pagination.documentsPerPage, email = searchEmail) => {
    if (!hasValidToken) return;

    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });
      if (email) {
        params.append('email', email);
      }
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_URL}/api/documents?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      if (!response.ok) {
        if (response.status === 401) {
          // Token is invalid, clear it and redirect to login
          console.log('Authentication failed, clearing token and redirecting to login...');
          userIsUnauthenticated();
          navigate(`/${lang}/login`);
          return;
        }
        throw new Error('Failed to fetch documents');
      }
      
      const result = await response.json();
      console.log(result);
      
      // Handle both new paginated response and legacy response for backward compatibility
      if (result.documents && result.pagination) {
        setDocuments(result.documents);
        setPagination(result.pagination);
      } else {
        // Legacy response - treat as first page
        setDocuments(Array.isArray(result) ? result : []);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalDocuments: Array.isArray(result) ? result.length : 0,
          documentsPerPage: Array.isArray(result) ? result.length : 0,
          hasNextPage: false,
          hasPrevPage: false
        });
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      setDocuments([]);
      setPagination(DEFAULT_PAGINATION);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchDocuments(newPage);
    }
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    fetchDocuments(1, pagination.documentsPerPage, searchEmail);
  };

  const handleLogout = () => {
    userIsUnauthenticated();
    navigate(`/${lang}/login`);
  };

  useEffect(() => {
    document.title = t("account")
  }, []);

  return (
    <div className="d-flex flex-column min-vh-100">
      <Header renderBg={true} className="py-2" />
      <div className="d-flex flex-column align-items-center py-2 px-5">
        <h2>{t("account")}</h2>
      </div>
      <div className="d-flex flex-column align-items-center py-2 px-5">
        <h3 className="mb-5">{t("doc_title")}</h3>
        {isAdmin && (
          <div className="d-flex mb-3" style={{ maxWidth: '400px', width: '100%' }}>
            <input
              type="text"
              className="form-control"
              placeholder="Search by email prefix..."
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Button variant="primary" onClick={handleSearch} className="ms-2">
              Search
            </Button>
          </div>
        )}
        {isLoading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
            <div className="spinner-border" role="status">
              <span className="visually-hidden">{t("loading")}</span>
            </div>
          </div>
        ) : documents && documents.length ? (
          <>
            <Table
              striped
              bordered
              hover
              size="sm"
              className={isAdmin ? "fs-6" : ""}
            >
              <thead>
                <tr key="header">
                  <th>#</th>
                  <th>{t("type")}</th>
                  <th>{t("status_header")}</th>
                  <th>{t("actions")}</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((item) => {
                  var id = item._id.toString(),
                    ctr = 0;
                  var timestamp = parseInt(id.slice(ctr, (ctr += 8)), 16);
                  var machineID = parseInt(id.slice(ctr, (ctr += 6)), 16);
                  var processID = parseInt(id.slice(ctr, (ctr += 4)), 16);
                  var counter = parseInt(id.slice(ctr, (ctr += 6)), 16);

                  return (
                    <tr key={id}>
                      <td>{isAdmin ? item.userMail : counter}</td>
                      <td>
                        <Icon
                          icon={productIcons[item.productName]}
                          width="2em"
                          height="2em"
                        />{" "}
                      </td>
                      <td> {t(item.status)}</td>
                      <td>
                        <DropdownButton id="actions" title="">
                          <Dropdown.Item
                            onClick={async () => {
                              if (item.status !== "ready") return;

                              try {
                                const response = await fetch(
                                  `${process.env.REACT_APP_API_SERVER_URL}/api/documents/pdf/${id}`,
                                  {
                                    headers: {
                                      Authorization: `Bearer ${token}`,
                                    },
                                  }
                                );

                                if (response.ok) {
                                  const blob = await response.blob();
                                  const url = window.URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = `document-${id}.pdf`;
                                  document.body.appendChild(a);
                                  a.click();
                                  window.URL.revokeObjectURL(url);
                                  document.body.removeChild(a);
                                } else {
                                  alert('Failed to download PDF');
                                }
                              } catch (error) {
                                console.error('Error downloading PDF:', error);
                                alert('Error downloading PDF');
                              }
                            }}
                            disabled={item.status !== "ready"}
                          >
                            {t("view")}
                          </Dropdown.Item>
                          {isAdmin && (
                            <Dropdown.Item target="_blank" href={`/${lang}/document/${id}`}>
                              {t("edit")}
                            </Dropdown.Item>
                          )}
                        </DropdownButton>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            
            {/* Pagination Component */}
            {pagination.totalPages > 1 && (
              <div className="d-flex justify-content-center align-items-center mt-3">
                <Pagination>
                  <Pagination.First
                    onClick={() => handlePageChange(1)}
                    disabled={!pagination.hasPrevPage}
                  >
                    {t("first")}
                  </Pagination.First>
                  <Pagination.Prev
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={!pagination.hasPrevPage}
                  >
                    {t("previous")}
                  </Pagination.Prev>
                  
                  {/* Show page numbers */}
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.currentPage >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = pagination.currentPage - 2 + i;
                    }
                    
                    return (
                      <Pagination.Item
                        key={pageNum}
                        active={pageNum === pagination.currentPage}
                        onClick={() => handlePageChange(pageNum)}
                      >
                        {pageNum}
                      </Pagination.Item>
                    );
                  })}
                  
                  <Pagination.Next
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={!pagination.hasNextPage}
                  >
                    {t("next")}
                  </Pagination.Next>
                  <Pagination.Last
                    onClick={() => handlePageChange(pagination.totalPages)}
                    disabled={!pagination.hasNextPage}
                  >
                    {t("last")}
                  </Pagination.Last>
                </Pagination>
              </div>
            )}
            
            {/* Pagination Info */}
            <div className="mt-2 text-muted small">
              {t("showing_documents", {
                start: Math.min((pagination.currentPage - 1) * pagination.documentsPerPage + 1, pagination.totalDocuments),
                end: Math.min(pagination.currentPage * pagination.documentsPerPage, pagination.totalDocuments),
                total: pagination.totalDocuments
              })}
            </div>
          </>
        ) : (
          t("no_docs")
        )}

        <Button
          variant="info"
          className="btn-white mt-5"
          size="lg"
          onClick={() => navigate(`/${lang}/products`)}
        >
          {t("newdoc_button")}
        </Button>
        <Button
          variant="danger"
          className="btn-white mt-3"
          size="lg"
          onClick={handleLogout}
        >
          {t("nav_signout")}
        </Button>
      </div>
      <Footer className="mt-auto" />
    </div>
  );
}
